import type preact from 'preact';
import { useCallback, useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { createStore, type State } from '../state/store';
import { useStore } from './useStore';
import { probe } from '../core/probe';
import { NM } from '../core/constants';
import { fmtNum } from '../core/util';
import type { BandRole } from '../core/types';
import type { MapViewProps } from '../map/MapView';
import { classifyRuntime, explainLoadFailure, throttleFault } from '../map/errors';
import { RenderPanel, StretchPanel, BandsPanel, bandsBadge } from './Panels';
import { isClassifying, trainedClasses } from '../render/style';
import { autoIndexRange } from '../export/classification';
import { Section } from './Section';
import { SourcePanel } from './Source';
import { ExportPanel } from './Export';
import { SignaturePlot, type Pin } from './Signature';
import { Guide } from './Guide';
import { Legend } from './Legend';
import { applySample, loadSamples, type Sample } from '../samples';
import { encodeState, decodeState, applyDecoded } from '../state/url';
import type { SignaturePoint } from '../render/sam';

/**
 * OpenLayers and proj4 together are most of the bundle and neither is needed until a
 * raster is actually opened, so they load on demand. First paint ships the shell.
 */
type MapModule = { MapView: (p: MapViewProps) => preact.JSX.Element };
type ProjModule = typeof import('../map/projections');
let mapMod: MapModule | null = null;
let projMod: ProjModule | null = null;
async function ensureMapModules(): Promise<{ map: MapModule; proj: ProjModule }> {
  if (!projMod) {
    projMod = await import('../map/projections');
    projMod.registerProjections();
  }
  if (!mapMod) mapMod = await import('../map/MapView') as MapModule;
  return { map: mapMod, proj: projMod };
}

export function App() {
  const store = useMemo(() => createStore(), []);
  const state = useStore(store);
  const [url, setUrl] = useState('');
  const [source, setSource] = useState<Record<string, unknown> | null>(null);
  const [hover, setHover] = useState<SignaturePoint[] | null>(null);
  const [pins, setPins] = useState<Pin[]>([]);
  const [readout, setReadout] = useState<{ role: string; value: number }[] | null>(null);
  const [samples, setSamples] = useState<Sample[]>([]);
  const [needsProj, setNeedsProj] = useState<number | null>(null);
  const [MapComp, setMapComp] = useState<MapModule | null>(mapMod);
  const [olAlphaBand, setOlAlphaBand] = useState<number | null>(null);
  const viewRef = useRef<{ fit: () => void; actualSize: () => void } | null>(null);
  const [guide, setGuide] = useState(false);
  const [panel, setPanel] = useState(false);
  const [noWebGL] = useState(() => {
    try {
      const c = document.createElement('canvas');
      return !(c.getContext('webgl2') ?? c.getContext('webgl'));
    } catch { return true; }
  });
  const pendingRef = useRef<Record<string, unknown> | null>(null);
  const pendingSample = useRef<Sample | null>(null);

  const say = useCallback((message: string, error = false, fix = '') =>
    store.set({ status: { message, error, fix } }), [store]);

  // Rendering faults surface inside OpenLayers' render loop or an unsettled promise,
  // where no try/catch of ours is on the stack. Both channels, or they are lost.
  useEffect(() => {
    const report = (err: unknown) => {
      const raw = classifyRuntime(err);
      const c = raw && throttleFault(raw);
      if (!c) return;
      say(c.msg, true, c.fix);
      if (c.kind === 'projection') {
        setNeedsProj(c.epsg ?? store.get().profile?.raster.epsg ?? null);
      }
    };
    const onErr = (e: ErrorEvent) => report(e.error ?? e.message);
    const onRej = (e: PromiseRejectionEvent) => report(e.reason);
    window.addEventListener('error', onErr);
    window.addEventListener('unhandledrejection', onRej);
    return () => {
      window.removeEventListener('error', onErr);
      window.removeEventListener('unhandledrejection', onRej);
    };
  }, [say, store]);

  useEffect(() => { void loadSamples().then(setSamples); }, []);
  useEffect(() => { document.documentElement.setAttribute('data-theme', state.theme); }, [state.theme]);

  // Keep the address bar in step, so the view is always shareable.
  useEffect(() => {
    const p = store.get().profile;
    if (!p) return;
    try { history.replaceState(null, '', '#' + encodeState(p, state)); } catch { /* ignore */ }
  }, [state, store]);

  const load = useCallback(async (
    open: () => Promise<unknown>, label: string, u: string, src: Record<string, unknown>,
  ) => {
    store.set({ busy: true });
    say(`opening ${label}`);
    store.set({ classes: [], activeClass: 0 });
    setPins([]);
    setNeedsProj(null);
    try {
      const profile = await probe(await open() as never, { url: u, onProgress: (m) => say(m) });
      const pend = pendingRef.current;
      pendingRef.current = null;
      let patch: Partial<State> = {};
      // A shared link carries its own bounds and must be reproduced exactly.
      let askedForRange = pend != null;
      if (pend) {
        const { state: st, roles } = applyDecoded(pend, profile);
        patch = st;
        roles.forEach((r, i) => {
          const b = profile.bands[i];
          if (r && b) {
            b.role = r as BandRole;
            b.wavelengthNm = NM[r as BandRole] ?? null;
            b.source = 'manual';
            b.why = 'restored from a shared link';
          }
        });
      } else if (pendingSample.current) {
        // The sample list knows things the file does not.
        const sample = pendingSample.current;
        patch = applySample(sample, profile);
        pendingSample.current = null;
        // A sample that names an index but no bounds gets the same auto stretch as any
        // other file; one that states min/max meant them.
        askedForRange = sample.min != null || sample.max != null;
      } else {
        // A sensible opening view for whatever bands the file turned out to have.
        const has = (r: string) => profile.bands.some((b) => b.role === r);
        if ((has('nir') || has('nir08')) && has('red')) {
          patch = { mode: 'index', indexId: 'ndvi', rampId: 'rdylgn', lo: -0.1, hi: 0.9 };
        } else if (has('red') && has('green') && has('blue')) {
          patch = { mode: 'index', indexId: 'vari', rampId: 'rdylgn', lo: -0.4, hi: 0.4 };
        } else {
          patch = { mode: 'single', singleBand: 0 };
        }
      }

      // Open on the spread the scene actually has, not the index's theoretical range.
      // Applies to every path that did not ask for specific bounds — the declared
      // range stays as the fallback when the data is degenerate.
      if (!askedForRange && (patch.mode === 'index' || patch.mode === 'single')) {
        const auto = autoIndexRange(profile, { ...store.get(), ...patch } as never);
        if (auto) { patch.lo = auto[0]; patch.hi = auto[1]; }
      }
      const { map, proj } = await ensureMapModules();
      setMapComp(map);
      setSource(src);
      const r = profile.raster;
      store.set({ profile, busy: false, ...patch });
      if (!proj.projectionKnown(r.epsg)) setNeedsProj(r.epsg);
      say(`${r.width}×${r.height} · ${r.samplesPerPixel} bands · ${r.sampleFormatName}${r.bitsPerSample[0]}`
        + ` · EPSG:${r.epsg ?? '?'} · ${r.isCOG ? 'valid COG' : 'NOT COG-structured — expect slow pans'}`);
    } catch (e) {
      const { msg, fix } = explainLoadFailure(e, u);
      store.set({ busy: false });
      say(msg, true, fix);
    }
  }, [store, say]);

  const openUrl = useCallback(async (u: string) => {
    const { fromUrl } = await import('geotiff');
    await load(() => fromUrl(u), u, u, { url: u });
  }, [load]);

  const openFile = useCallback(async (f: File) => {
    const { fromBlob } = await import('geotiff');
    await load(() => fromBlob(f), f.name, `file://${f.name}`, { blob: f });
  }, [load]);

  // Restore a shared view on first paint.
  useEffect(() => {
    const dec = decodeState(location.hash);
    if (!dec) return;
    pendingRef.current = dec.raw;
    setUrl(dec.url);
    if (/^https?:/.test(dec.url)) void openUrl(dec.url);
    else say('This link points at a local file — reopen it with the file picker.', true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const p = state.profile;
  const grouped = samples.reduce<Record<string, Sample[]>>((acc, s) => {
    (acc[s.group ?? 'Samples'] ??= []).push(s);
    return acc;
  }, {});

  return (
    <div class="app">
      <header class="top">
        <div class="brand"><b>Spectral Mapper</b></div>
        <div class="entry">
          {samples.length > 0 && (
            <select class="samples" aria-label="Sample datasets" value=""
              onChange={(e) => {
                const el = e.target as HTMLSelectElement;
                const s = samples[+el.value];
                el.value = '';
                if (!s) return;
                setUrl(s.url);
                if (s.description) say(s.description);
                pendingSample.current = s;
                void openUrl(s.url);
              }}>
              <option value="">Sample datasets…</option>
              {Object.entries(grouped).map(([g, list]) => (
                <optgroup label={g} key={g}>
                  {list.map((s) => (
                    <option key={s.url} value={samples.indexOf(s)} title={s.description}>{s.name}</option>
                  ))}
                </optgroup>
              ))}
            </select>
          )}
          <input type="url" value={url}
            placeholder="https://…/scene.tif — needs CORS + range requests"
            onInput={(e) => setUrl((e.target as HTMLInputElement).value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && url.trim()) void openUrl(url.trim()); }} />
          <button class="btn go" disabled={state.busy}
            onClick={() => { if (url.trim()) void openUrl(url.trim()); }}>Load</button>
          <label class="btn file">Open local file
            <input type="file" accept=".tif,.tiff" onChange={(e) => {
              const f = (e.target as HTMLInputElement).files?.[0];
              if (f) void openFile(f);
            }} />
          </label>
          <button class="btn" title="Copy a link that reopens this exact view" onClick={() => {
            if (!p) { say('Load a raster first.', true); return; }
            void navigator.clipboard.writeText(location.href).then(() => say('Link copied.'));
          }}>Share</button>
          <button class="btn" data-testid="guide-open" title="Converting and hosting your own files"
            onClick={() => setGuide(true)}>Guide</button>
          <a class="btn" href="mailto:info@spectralmapper.com" title="Questions, bugs, sample datasets">Contact</a>
          <button class="btn" onClick={() =>
            store.set({ theme: state.theme === 'light' ? 'dark' : 'light' })}>
            {state.theme === 'light' ? 'Dark' : 'Light'}
          </button>
        </div>
      </header>

      <div class={'status' + (state.status.error ? ' err' : '')}>
        <span>{state.status.message}</span>
        {state.status.fix && <span class="fix">{state.status.fix}</span>}
        {state.status.error && /CORS|byte-range|COG|tiled|overview/i.test(
          state.status.message + state.status.fix) && (
          <button class="btn tiny" onClick={() => setGuide(true)}>How to fix this</button>
        )}
        {needsProj != null && (
          <button class="btn tiny" onClick={() => {
            const code = needsProj;
            void ensureMapModules().then(({ proj }) => proj.fetchProjection(code))
              .then(() => {
                setNeedsProj(null);
                say(`EPSG:${code} registered — reload the file to redraw.`);
              })
              .catch((e) => say(`Could not fetch EPSG:${code}: ${(e as Error).message}`, true,
                'Reproject the file instead: gdalwarp -t_srs EPSG:3857 in.tif out.tif'));
          }}>Fetch EPSG:{needsProj} definition</button>
        )}
      </div>

      <main class="wrap">
        {/* Only rendered narrow; on a phone the controls are a sheet over the map,
            because splitting a 700 px screen leaves neither half usable. */}
        <button class={'sheet-tab' + (panel ? ' open' : '')} data-testid="sheet-toggle"
          onClick={() => setPanel(!panel)}>{panel ? 'Close' : 'Controls'}</button>
        <aside class={'side' + (panel ? ' open' : '')}>
          <button class="sheet-grip" aria-label="Close controls"
            onClick={() => setPanel(false)} />
          {!p && <p class="hint">Load a raster, or pick a sample, to see its bands.</p>}
          {p && (
            <>
              <Section id="render" title="Render" store={store} state={state}>
                <RenderPanel store={store} state={state} profile={p} />
                <div class="btnrow">
                  <button class="btn" data-testid="fit"
                    onClick={() => viewRef.current?.fit()}>Zoom to raster</button>
                  <button class="btn" data-testid="actual-size"
                    title="One screen pixel per raster pixel"
                    onClick={() => viewRef.current?.actualSize()}>1:1</button>
                </div>
              </Section>
              <Section id="stretch" title="Stretch" store={store} state={state}>
                <StretchPanel store={store} state={state} profile={p} />
              </Section>
              <Section id="bands" title="Bands" store={store} state={state}
                extra={<span class={'tag ' + (bandsBadge(p).named ? 'metadata' : 'guess')}>
                  {bandsBadge(p).label}</span>}>
                <BandsPanel store={store} state={state} profile={p} />
              </Section>
              <Section id="export" title="Export" store={store} state={state}>
                <ExportPanel state={state} profile={p} onStatus={say} />
              </Section>
              <Section id="source" title="Source" store={store} state={state}>
                <SourcePanel store={store} state={state} profile={p} olAlphaBand={olAlphaBand} />
              </Section>
            </>
          )}
        </aside>

        <div class="map-col">
          {p && source && needsProj == null && MapComp && !noWebGL ? (
            <MapComp.MapView store={store} onReady={(view, alphaBand) => {
                viewRef.current = view;
                setOlAlphaBand(alphaBand);
              }} state={state} profile={p} source={source}
              onPin={(pin) => {
                // Classifying: a click trains the selected class. Otherwise it pins a
                // spectrum for comparison, which is what the plot is for the rest of
                // the time.
                if (!isClassifying(state)) {
                  setPins((cur) => [...cur, pin].slice(-6));
                  return;
                }
                const cls = state.classes;
                if (!cls.length) {
                  say('Add a class first, then click examples of it on the map.', true);
                  return;
                }
                const i = Math.min(state.activeClass, cls.length - 1);
                store.set({
                  classes: cls.map((c, k) =>
                    (k === i ? { ...c, points: [...c.points, pin.pts] } : c)),
                });
              }}
              onBrush={(batch) => {
                const cls = state.classes;
                if (!isClassifying(state) || !cls.length) return;
                const i = Math.min(state.activeClass, cls.length - 1);
                store.set({
                  classes: cls.map((c, k) => (k === i
                    // Capped: a long drag can gather thousands, and past a few hundred
                    // the class mean has stopped moving.
                    ? { ...c, points: [...c.points, ...batch].slice(-600) }
                    : c)),
                });
              }}
              onHover={(pts, ro) => { setHover(pts); setReadout(ro); }}
              onFault={(m) => say(m, true)} />
          ) : (
            <div class="splash"><div>
              <b>{noWebGL ? 'WebGL unavailable'
                : p ? 'Cannot place this on a map' : 'Nothing loaded'}</b>
              {noWebGL
                ? 'The file parses correctly — bands, histograms, statistics and the value domain are all in the sidebar. Only the map needs a GPU.'
                : p
                  ? 'The raster read fine — bands, statistics and warnings are in the sidebar. Only the map placement failed.'
                  : <>Paste a COG URL above, pick a sample, or open a local file. Only the
                    headers and the smallest overview are read — a few hundred KB
                    whatever the file size.
                    {' '}<a href="#" onClick={(e) => { e.preventDefault(); setGuide(true); }}>
                    Converting your own imagery →</a></>}
            </div></div>
          )}
          {p && source && needsProj == null && MapComp && !noWebGL &&
            <Legend state={state} profile={p} />}
          {/* Classification with nothing trained renders a fully transparent layer,
              which looks identical to a broken one. Say which it is. */}
          {p && source && MapComp && !noWebGL && isClassifying(state)
            && trainedClasses(state).length === 0 && (
            <div class="ov untrained" data-testid="untrained">
              <b>No classes trained yet</b>
              The image below is the file's own true colour. Click a patch of
              {' '}<b>{state.classes[state.activeClass]?.name ?? 'a class'}</b> to train it,
              then add a class for each further material.
            </div>
          )}
          {readout && (
            <div class="ov readout">
              <h3>At cursor</h3>
              <table><tbody>
                {readout.map((r, i) => (
                  <tr key={i}><td>{i + 1} {r.role}</td><td>{fmtNum(r.value)}</td></tr>
                ))}
              </tbody></table>
            </div>
          )}
          <SignaturePlot classes={state.classes} pins={pins} hover={hover}
            active={!!p && !!source && needsProj == null && !!MapComp && !noWebGL}
            onRemovePin={(i) => setPins((cur) => cur.filter((_, k) => k !== i))}
            onClear={() => {
              setPins([]);
              store.set({ classes: state.classes.map((c) => ({ ...c, points: [] })) });
            }} />
        </div>
      </main>
      {guide && <Guide onClose={() => setGuide(false)} />}
    </div>
  );
}
